import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import LoadingOverlay from '../../components/ui/LoadingOverlay';
import PersonalInfoSection from './components/PersonalInfoSection';
import ContactInfoSection from './components/ContactInfoSection';
import AddressSection from './components/AddressSection';
import ProfileImageUpload from './components/ProfileImageUpload';
import FormActions from './components/FormActions';
import SuccessModal from './components/SuccessModal';

const MemberRegistration = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [registeredMember, setRegisteredMember] = useState(null);

  // Mock de membros existentes para validação de duplicidade
  const existingMembers = [
    { cpf: "123.456.789-00", voterId: "VID001", email: "john@example.com" },
    { cpf: "987.654.321-00", voterId: "VID002", email: "jane@example.com" },
    { cpf: "456.789.123-00", voterId: "VID003", email: "bob@example.com" }
  ];

  const [formData, setFormData] = useState({
    fullName: '',
    dateOfBirth: '',
    cpf: '',
    voterId: '',
    gender: '',
    occupation: '',
    emails: [{ address: '', type: 'Principal' }],
    phones: [{ number: '', type: 'Principal' }],
    addresses: [{
      street: '',
      city: '',
      state: '',
      zipCode: '',
      type: 'Home'
    }],
    profileImage: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const addEmail = () => {
    setFormData(prev => ({
      ...prev,
      emails: [...prev?.emails, { address: '', type: 'Secundário' }]
    }));
  };

  const removeEmail = (index) => {
    setFormData(prev => ({
      ...prev,
      emails: prev?.emails?.filter((_, i) => i !== index)
    }));
  };

  const addPhone = () => {
    setFormData(prev => ({
      ...prev,
      phones: [...prev?.phones, { number: '', type: 'Secundário' }]
    }));
  };

  const removePhone = (index) => {
    setFormData(prev => ({
      ...prev,
      phones: prev?.phones?.filter((_, i) => i !== index)
    }));
  };

  const addAddress = () => {
    setFormData(prev => ({
      ...prev,
      addresses: [...prev?.addresses, {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        type: 'Secundário'
      }]
    }));
  };

  const removeAddress = (index) => {
    setFormData(prev => ({
      ...prev,
      addresses: prev?.addresses?.filter((_, i) => i !== index)
    }));
  };

  const validateCPF = (cpf) => {
    const cleanCPF = cpf?.replace(/\D/g, '');
    if (cleanCPF?.length !== 11) return false;
    
    // Check if all digits are the same
    if (/^(\d)\1{10}$/?.test(cleanCPF)) return false;
    
    // Validate CPF algorithm
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cleanCPF?.charAt(i)) * (10 - i);
    }
    let remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cleanCPF?.charAt(9))) return false;
    
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cleanCPF?.charAt(i)) * (11 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    return remainder === parseInt(cleanCPF?.charAt(10));
  };

  const validateForm = () => {
    const newErrors = {};

    // Validação de informações pessoais
    if (!formData?.fullName?.trim()) {
      newErrors.fullName = 'Nome completo é obrigatório';
    }

    if (!formData?.dateOfBirth) {
      newErrors.dateOfBirth = 'Data de nascimento é obrigatória';
    }

    if (!formData?.cpf?.trim()) {
      newErrors.cpf = 'CPF é obrigatório';
    } else if (!validateCPF(formData?.cpf)) {
      newErrors.cpf = 'Formato de CPF inválido';
    } else if (existingMembers?.some(member => member?.cpf === formData?.cpf)) {
      newErrors.cpf = 'CPF já existe no sistema';
    }

    if (!formData?.voterId?.trim()) {
      newErrors.voterId = 'Título de eleitor é obrigatório';
    } else if (existingMembers?.some(member => member?.voterId === formData?.voterId)) {
      newErrors.voterId = 'Título de eleitor já existe no sistema';
    }

    // Validação de e-mail
    const emailErrors = [];
    formData?.emails?.forEach((email, index) => {
      if (index === 0 && !email?.address?.trim()) {
        emailErrors[index] = 'E-mail principal é obrigatório';
      } else if (email?.address && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email?.address)) {
        emailErrors[index] = 'Formato de e-mail inválido';
      } else if (email?.address && existingMembers?.some(member => member?.email === email?.address)) {
        emailErrors[index] = 'E-mail já existe no sistema';
      }
    });
    if (emailErrors?.length > 0) {
      newErrors.emails = emailErrors;
    }

    // Validação de telefone
    const phoneErrors = [];
    formData?.phones?.forEach((phone, index) => {
      if (index === 0 && !phone?.number?.trim()) {
        phoneErrors[index] = 'Telefone principal é obrigatório';
      }
    });
    if (phoneErrors?.length > 0) {
      newErrors.phones = phoneErrors;
    }

    // Validação de endereço
    const addressErrors = [];
    formData?.addresses?.forEach((address, index) => {
      const addressError = {};
      if (index === 0) {
        if (!address?.street?.trim()) addressError.street = 'Endereço é obrigatório';
        if (!address?.city?.trim()) addressError.city = 'Cidade é obrigatória';
        if (!address?.state?.trim()) addressError.state = 'Estado é obrigatório';
        if (!address?.zipCode?.trim()) addressError.zipCode = 'CEP é obrigatório';
      }
      if (Object.keys(addressError)?.length > 0) {
        addressErrors[index] = addressError;
      }
    });
    if (addressErrors?.length > 0) {
      newErrors.addresses = addressErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Cria objeto do membro com ID e timestamp
      const newMember = {
        id: Date.now(),
        ...formData,
        registrationDate: new Date()?.toISOString(),
        status: 'Ativo'
      };

      setRegisteredMember(newMember);
      setShowSuccessModal(true);
      
    } catch (error) {
      console.error('Falha no cadastro:', error);
      setErrors({ submit: 'Falha no cadastro. Tente novamente.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      fullName: '',
      dateOfBirth: '',
      cpf: '',
      voterId: '',
      gender: '',
      occupation: '',
      emails: [{ address: '', type: 'Principal' }],
      phones: [{ number: '', type: 'Principal' }],
      addresses: [{
        street: '',
        city: '',
        state: '',
        zipCode: '',
        type: 'Residencial'
      }],
      profileImage: ''
    });
    setErrors({});
  };

  const handleAddAnother = () => {
    setShowSuccessModal(false);
    setRegisteredMember(null);
    handleReset();
  };

  const isFormValid = () => {
    return formData?.fullName?.trim() &&
           formData?.dateOfBirth &&
           formData?.cpf?.trim() &&
           formData?.voterId?.trim() &&
           formData?.emails?.[0]?.address?.trim() &&
           formData?.phones?.[0]?.number?.trim() &&
           formData?.addresses?.[0]?.street?.trim() &&
           formData?.addresses?.[0]?.city?.trim() &&
           formData?.addresses?.[0]?.state?.trim() &&
           formData?.addresses?.[0]?.zipCode?.trim();
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Breadcrumb />
          
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-2">
              Cadastro de Membro
            </h1>
            <p className="text-muted-foreground">
              Adicione um novo filiado à base com informações pessoais completas.
            </p>
          </div>

          {errors?.submit && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
              <p className="text-error text-sm">{errors?.submit}</p>
            </div>
          )}

          <div className="space-y-6">
            <PersonalInfoSection
              formData={formData}
              errors={errors}
              onChange={handleChange}
            />

            <ContactInfoSection
              formData={formData}
              errors={errors}
              onChange={handleChange}
              onAddEmail={addEmail}
              onRemoveEmail={removeEmail}
              onAddPhone={addPhone}
              onRemovePhone={removePhone}
            />

            <AddressSection
              formData={formData}
              errors={errors}
              onChange={handleChange}
              onAddAddress={addAddress}
              onRemoveAddress={removeAddress}
            />

            <ProfileImageUpload
              formData={formData}
              onChange={handleChange}
              error={errors?.profileImage}
            />

            <FormActions
              onSubmit={handleSubmit}
              onReset={handleReset}
              isSubmitting={isSubmitting}
              isValid={isFormValid()}
            />
          </div>
        </div>
      </main>
      <LoadingOverlay
        isVisible={isSubmitting}
        message="Cadastrando novo membro..."
        type="processing"
      />
      <SuccessModal
        isVisible={showSuccessModal}
        memberData={registeredMember}
        onClose={() => setShowSuccessModal(false)}
        onAddAnother={handleAddAnother}
      />
    </div>
  );
};

export default MemberRegistration;